import useAuth from "src/hooks/useAuth";

type BaseParams = {
    controller: string
}

type GetBaseResultParams<T, G> = BaseParams & {
    params?: any
    mapperDTOToModel: (dto: G) => T
}

type PutBaseResultParams<T, G> = BaseParams & {
    mapperModelToDTO: (model: T) => Partial<G>
    mapperDTOToModel: (dto: G) => T
}

type PutBaseBooleanResultParams = BaseParams & {
    body: any
}

type PostBaseResultParams<T, G> = BaseParams & {
    mapperModelToDTO: (model: T) => Partial<G>
    mapperDTOToModel: (dto: G) => T
}

type DeleteBaseBooleanResultParams = BaseParams & {
    params: any
}

type ActionBaseResultParams = BaseParams & {
    action: string
    params: any
}

export default function useBaseDataContext(route: string) {
    const { axios } = useAuth();

    function getBaseResult<T, G>({
        controller,
        params,
        mapperDTOToModel
    }: GetBaseResultParams<T, G>) {
        return new Promise<T>((resolve, reject) => {
            axios.get(`/mid/${route}/${controller}/`, { 
                params: params
             })
                .then(response => {
                    const { data, error } = response.data;
                    if (error) reject(error);
                    else resolve(mapperDTOToModel(data as G));
                }).catch(err => {
                    reject(err.message);
                });
        })
    }

    function getBaseResultArray<T, G>({
        mapperDTOToModel,
        ...others
    }: GetBaseResultParams<T, G>) {
        return getBaseResult<T[], G[]>({
            ...others,
            mapperDTOToModel: (r: G[]) => r.map(mapperDTOToModel)
        });
    }

    function putBaseResult<T, G>({
        controller,
        mapperModelToDTO,
        mapperDTOToModel
    }: PutBaseResultParams<T, G>) {
        return (model: Partial<T>) => new Promise<T>((resolve, reject) => { 
            let body = mapperModelToDTO(model as T);
            axios.put(`/mid/${route}/${controller}/`, body)
                .then(response => {
                    const { data, error } = response.data;
                    if (error) reject(error);
                    else resolve(mapperDTOToModel(data as G));
                }).catch(err => {
                    reject(err.message);
                });
         });
    }

    function putBaseBooleanResult({
        controller,
        body
    }: PutBaseBooleanResultParams) {
        return new Promise<boolean>((resolve, reject) => { 
            axios.put(`/mid/${route}/${controller}/`, body)
                .then(response => {
                    const { data, error } = response.data;
                    if (error) reject(error);
                    else resolve(data);
                }).catch(err => {
                    reject(err.message);
                });
         });
    }

    function postBaseResult<T, G>({
        controller,
        mapperModelToDTO,
        mapperDTOToModel
    }: PostBaseResultParams<T, G>) {
        return (model: Partial<T>) => new Promise<T>((resolve, reject) => {
            let body = mapperModelToDTO(model as T);
            axios.post(`/mid/${route}/${controller}/`, body)
                .then(response => {
                    const { data, error } = response.data;
                    if (error) reject(error);
                    else resolve(mapperDTOToModel(data as G));
                }).catch(err => {
                    reject(err.message);
                });
        });
    }

    function deleteBaseBooleanResult({
        controller,
        params
    }: DeleteBaseBooleanResultParams) {
        return new Promise<boolean>((resolve, reject) => {
            axios.delete(`/mid/${route}/${controller}/`, { 
                params: params
             })
                .then(response => {
                    const { data, error } = response.data;
                    if (error) reject(error);
                    else resolve(data);
                }).catch(err => {
                    reject(err.message);
                });
        })
    }

    function actionBaseResult({
        controller,
        action,
        params
    }: ActionBaseResultParams) {
        return new Promise<void>((resolve, reject) => {
            axios.post(`/mid/${route}/${controller}/${action}`, params)
                .then(response => {
                    const { error } = response.data;
                    if (error) reject(error);
                    else resolve();
                });
        });
    }

    return {
        getBaseResult,
        getBaseResultArray,
        putBaseResult,
        putBaseBooleanResult,
        postBaseResult,
        deleteBaseBooleanResult,
        actionBaseResult
    }
}